/** QL-691 — the one parser for Drizzle `mode: "string"` timestamps, shared by API and web. */

/**
 * Parse a timestamp that came out of the database.
 *
 * Drizzle's `mode: "string"` timestamp columns hand back Postgres' own text
 * form of a `timestamp without time zone` — `"2026-08-31 14:00:11.723"`, with
 * no `T` and no offset. The server runs Postgres in UTC (`SHOW timezone` →
 * `Etc/UTC`), so that wall-clock IS UTC, but every JS engine parses a
 * space-separated, offset-less string as LOCAL time. Measured in V8 at
 * Europe/Oslo: the string above reads as `12:00:11Z` — two hours early.
 *
 * Left alone, that skew pushes rows across the midnight boundaries these
 * filters are built on. So: when a string carries no timezone designator,
 * read it as UTC, which is what it actually is. A string that already has one
 * (`Z` or `±HH:MM`) is parsed as-is.
 *
 * Returns null when the value is missing or unparseable, so callers decide
 * what an unknown timestamp means rather than silently getting epoch 0.
 */
export function parseDbTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.trim().replace(" ", "T");
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(trimmed);
  // Postgres can abbreviate a whole-hour offset to `+02`. Neither `+02` nor
  // `+02Z` parses, so the short form is widened to `+02:00` rather than being
  // appended to and rejected. Found by CodeRabbit CLI.
  const hasShortZone = /T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?[+-]\d{2}$/.test(trimmed);
  const normalised = hasZone ? trimmed : hasShortZone ? `${trimmed}:00` : `${trimmed}Z`;
  const parsed = new Date(normalised);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * True when a stored timestamp is strictly before `now` (an epoch-ms instant,
 * the wall clock by default). Compares instants via `parseDbTimestamp` —
 * never the raw strings, whose space-vs-`T` shapes don't sort like their
 * instants (QL-505) — so web and API share one definition of "past".
 *
 * Missing or unparseable input is NOT past: there is nothing to expire
 * against, so callers treat it like an absent deadline. That fails OPEN — a
 * caller gating access on a value an end user can influence should check
 * `parseDbTimestamp(...) === null` itself rather than rely on this default.
 */
export function isPastDbTimestamp(
  value: string | null | undefined,
  now: number = Date.now(),
): boolean {
  const parsed = parseDbTimestamp(value);
  return parsed !== null && parsed.getTime() < now;
}
